import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

export default function AdminBrochure({ onSave }: Props) {
  const [form, setForm] = useState({
    pageTitle: "Investment Brochure",
    pageSubtitle: "Thorpe Dellow Social Housing Structured Product",
    heroHeadline: "Social Housing Structured Product",
    heroSub: "A comprehensive overview of the Thorpe Dellow 5-Year Social Housing Structured Product — targeting 10.00% per annum, distributed monthly, backed by UK assisted living assets.",
    stickyButtonLabel: "Download Brochure",
    stickyButtonVisible: true,
    printButtonLabel: "Save as PDF",
    printButtonVisible: true,
    footerDisclaimer: "This brochure is issued in accordance with Section 21 of the Financial Services and Markets Act 2000 (FSMA). Intended solely for Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors. Capital at risk.",
    stat1Value: "10.00%",
    stat1Label: "Target Per Annum",
    stat2Value: "5 Years",
    stat2Label: "Investment Term",
    stat3Value: "Monthly",
    stat3Label: "Income Frequency",
    stat4Value: "1st Charge",
    stat4Label: "Asset Security",
    stat5Value: "£10,000",
    stat5Label: "Minimum Investment",
    stat6Value: "GBP",
    stat6Label: "Currency",
  });

  const update = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Brochure Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage the investment brochure page content, sticky download button, and PDF export settings.</p>
      </div>

      <AdminCard title="Page Header">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Page Title</label>
              <input value={form.pageTitle} onChange={(e) => update("pageTitle", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Page Subtitle</label>
              <input value={form.pageSubtitle} onChange={(e) => update("pageSubtitle", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Hero Headline</label>
            <input value={form.heroHeadline} onChange={(e) => update("heroHeadline", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Hero Subtext</label>
            <textarea value={form.heroSub} onChange={(e) => update("heroSub", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Key Stats (6 Metrics)">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map((n) => (
            <div key={n} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <p className="text-xs font-bold text-slate-500 mb-2">Stat {n}</p>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Value</label>
                  <input
                    value={form[`stat${n}Value` as keyof typeof form] as string}
                    onChange={(e) => update(`stat${n}Value`, e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Label</label>
                  <input
                    value={form[`stat${n}Label` as keyof typeof form] as string}
                    onChange={(e) => update(`stat${n}Label`, e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard title="Download Buttons">
        <div className="space-y-5">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div>
              <p className="text-sm font-semibold text-[#071A2E]">Sticky Download Button</p>
              <p className="text-xs text-slate-400 mt-0.5">Floating button on the right side of all pages</p>
            </div>
            <button
              onClick={() => update("stickyButtonVisible", !form.stickyButtonVisible)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${form.stickyButtonVisible ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
            >
              <i className={`${form.stickyButtonVisible ? "ri-eye-line" : "ri-eye-off-line"} leading-none`} />
              {form.stickyButtonVisible ? "Visible" : "Hidden"}
            </button>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sticky Button Label</label>
            <input value={form.stickyButtonLabel} onChange={(e) => update("stickyButtonLabel", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div>
              <p className="text-sm font-semibold text-[#071A2E]">PDF Export Button</p>
              <p className="text-xs text-slate-400 mt-0.5">Floating button on the brochure page itself</p>
            </div>
            <button
              onClick={() => update("printButtonVisible", !form.printButtonVisible)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${form.printButtonVisible ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
            >
              <i className={`${form.printButtonVisible ? "ri-eye-line" : "ri-eye-off-line"} leading-none`} />
              {form.printButtonVisible ? "Visible" : "Hidden"}
            </button>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">PDF Button Label</label>
            <input value={form.printButtonLabel} onChange={(e) => update("printButtonLabel", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Footer Disclaimer">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Regulatory Disclaimer Text</label>
          <textarea value={form.footerDisclaimer} onChange={(e) => update("footerDisclaimer", e.target.value)} rows={4} maxLength={500} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          <p className="text-right text-xs text-slate-400 mt-1">{form.footerDisclaimer.length}/500</p>
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
